import { ArtifactLogo } from "@/components";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useAuthStore } from "@/store";
import { useThreadStore } from "@/store/threadStore";
import { Loader2, PenSquare, SearchIcon } from "lucide-react";
import PropTypes from "prop-types";
import * as React from "react";

export function SearchDialog({
  isOpen,
  setIsOpen,
  handleThreadSelect,
  handleNewChat,
}) {
  const [dialogSearchQuery, setDialogSearchQuery] = React.useState("");
  const [freshThreads, setFreshThreads] = React.useState([]);
  const [isLoadingThreads, setIsLoadingThreads] = React.useState(false);
  const dialogInputRef = React.useRef(null);
  const { fetchUserThreads } = useThreadStore();
  const { userId } = useAuthStore();

  // Fetch fresh threads when dialog opens
  React.useEffect(() => {
    if (isOpen && userId) {
      const loadFreshThreads = async () => {
        try {
          setIsLoadingThreads(true);

          // Get fresh threads directly from the database
          const threads = await fetchUserThreads(userId, {
            preventAutomaticCreation: true,
            setCurrentThread: true, // Allow setting current thread when refreshing sidebar
          });

          setFreshThreads(threads || []);
        } catch (error) {
          console.error("Error loading fresh threads for search:", error);
          // Fallback to current threads from store if fetch fails
          const { threads: currentThreads = [] } =
            useThreadStore.getState?.() || { threads: [] };
          setFreshThreads(currentThreads);
        } finally {
          setIsLoadingThreads(false);
        }
      };

      loadFreshThreads();
    }
  }, [isOpen, userId, fetchUserThreads]);

  // Group threads by date
  const groupThreadsByDate = () => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const prevSevenDays = new Date(today);
    prevSevenDays.setDate(prevSevenDays.getDate() - 7);

    // Filter threads based on search query using fresh threads
    const filteredThreads = freshThreads.filter(
      (thread) =>
        !dialogSearchQuery.trim() ||
        thread.title?.toLowerCase().includes(dialogSearchQuery.toLowerCase()) ||
        thread.id?.toLowerCase().includes(dialogSearchQuery.toLowerCase())
    );

    // Group threads
    const groupedThreads = {
      yesterday: [],
      prevSevenDays: [],
    };

    filteredThreads.forEach((thread) => {
      if (!thread?.updated_at) return;

      try {
        const threadDate = new Date(thread.updated_at);
        if (isNaN(threadDate.getTime())) return;

        if (threadDate.toDateString() === yesterday.toDateString()) {
          groupedThreads.yesterday.push(thread);
        } else if (threadDate >= prevSevenDays) {
          groupedThreads.prevSevenDays.push(thread);
        }
      } catch (err) {
        console.error("Error parsing thread date:", err);
      }
    });

    return groupedThreads;
  };

  const groupedThreads = groupThreadsByDate();

  React.useEffect(() => {
    // Focus the search input when dialog opens
    if (isOpen && dialogInputRef.current) {
      setTimeout(() => dialogInputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Clear search query when dialog closes
  React.useEffect(() => {
    if (!isOpen) {
      setDialogSearchQuery("");
    }
  }, [isOpen]);

  const handleThreadClick = (thread) => {
    handleThreadSelect(thread);
    setIsOpen(false);
  };

  const handleCreateNewChat = () => {
    handleNewChat();
    setIsOpen(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent
        className="p-0 gap-0 max-w-md sm:max-w-lg md:max-w-xl bg-background/80 backdrop-blur-md border border-border/50 text-foreground z-[400] rounded-2xl"
        hideCloseButton={true}
        style={{ zIndex: 9999 }}
        forceMount
      >
        <DialogTitle className="sr-only">Search chats</DialogTitle>
        <DialogDescription className="sr-only">
          Search through your chat history or start a new chat
        </DialogDescription>
        <div className="flex items-center p-4 border-b border-border">
          <SearchIcon className="h-5 w-5 text-muted-foreground mr-2 flex-shrink-0 svg-icon" />
          <Input
            ref={dialogInputRef}
            value={dialogSearchQuery}
            onChange={(e) => setDialogSearchQuery(e.target.value)}
            placeholder="Search chats..."
            className="border-0 bg-transparent text-foreground placeholder:text-muted-foreground focus-visible:ring-0 focus-visible:ring-offset-0 text-base"
          />
        </div>

        <ScrollArea className="max-h-[80vh] [&>[data-radix-scroll-area-scrollbar]]:!left-0 [&>[data-radix-scroll-area-scrollbar]]:!right-auto [&>[data-radix-scroll-area-scrollbar]]:bg-sidebar-accent [&>[data-radix-scroll-area-scrollbar]>[data-radix-scroll-area-thumb]]:bg-sidebar-accent-foreground/20 [&>[data-radix-scroll-area-scrollbar]>[data-radix-scroll-area-thumb]]:hover:bg-sidebar-accent-foreground/30 [&>[data-radix-scroll-area-scrollbar]]:w-2">
          <div className="px-2">
            {/* New Chat Option */}
            <Button
              variant="ghost"
              className="w-full justify-start text-foreground bg-transparent hover:bg-accent rounded-md p-3 mb-4"
              onClick={handleCreateNewChat}
            >
              <PenSquare className="h-5 w-5 mr-3 text-muted-foreground" />
              <span className="font-medium">New chat</span>
            </Button>

            {/* Loading state */}
            {isLoadingThreads && (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                <span className="ml-2 text-muted-foreground text-sm">
                  Loading chats...
                </span>
              </div>
            )}

            {/* Thread results - only show when not loading */}
            {!isLoadingThreads && (
              <>
                {/* Yesterday */}
                {groupedThreads.yesterday.length > 0 && (
                  <>
                    <div className="px-3 py-2 text-xs font-medium text-muted-foreground">
                      Yesterday
                    </div>
                    {groupedThreads.yesterday.map((thread) => (
                      <Button
                        key={thread.id}
                        variant="ghost"
                        className="w-full justify-start text-foreground bg-transparent hover:bg-accent rounded-md p-3"
                        onClick={() => handleThreadClick(thread)}
                      >
                        <div className="h-5 w-5 mr-3 flex items-center justify-center">
                          <ArtifactLogo className="h-5 w-5" />
                        </div>
                        <span className="truncate font-normal">
                          {thread.title || thread.id}
                        </span>
                      </Button>
                    ))}
                  </>
                )}

                {/* Previous 7 Days */}
                {groupedThreads.prevSevenDays.length > 0 && (
                  <>
                    <div className="px-3 py-2 text-xs font-medium text-muted-foreground mt-2">
                      Previous 7 Days
                    </div>
                    {groupedThreads.prevSevenDays.map((thread) => (
                      <Button
                        key={thread.id}
                        variant="ghost"
                        className="w-full justify-start text-foreground bg-transparent hover:bg-accent rounded-md p-3"
                        onClick={() => handleThreadClick(thread)}
                      >
                        <div className="h-5 w-5 mr-3 flex items-center justify-center">
                          <ArtifactLogo className="h-5 w-5" />
                        </div>
                        <span className="truncate font-normal">
                          {thread.title || thread.id}
                        </span>
                      </Button>
                    ))}
                  </>
                )}

                {/* No results */}
                {dialogSearchQuery &&
                  groupedThreads.yesterday.length === 0 &&
                  groupedThreads.prevSevenDays.length === 0 && (
                    <div className="px-3 py-6 text-center text-muted-foreground">
                      No chats found matching &quot;{dialogSearchQuery}&quot;
                    </div>
                  )}

                {/* No threads at all */}
                {!dialogSearchQuery && freshThreads.length === 0 && (
                  <div className="px-3 py-6 text-center text-muted-foreground">
                    No chats found
                  </div>
                )}
              </>
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}

SearchDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  setIsOpen: PropTypes.func.isRequired,
  handleThreadSelect: PropTypes.func.isRequired,
  handleNewChat: PropTypes.func.isRequired,
};
